// Company Profile API
import { request } from "./base";

// Company Profile Types
export type ProProfile = {
	id: number;
	proId: string;
	tagline: string | null;
	about: string | null;
	establishedYear: number | null;
	teamSizeCategory: "solo" | "2-5" | "6-10" | "11-25" | "25+" | null;
	areasServed: string[] | null;
	processDescription: string | null;
	instagramHandle: string | null;
	facebookUrl: string | null;
	youtubeUrl: string | null;
	googleBusinessUrl: string | null;
	websiteUrl: string | null;
	linkedinUrl: string | null;
	twitterUrl: string | null;
	pinterestUrl: string | null;
	dateCreated: string;
	dateUpdated: string;
};

export type ProLeadership = {
	id: number;
	proId: string;
	name: string;
	role: string;
	bio: string | null;
	photoUrl: string | null;
	linkedinUrl: string | null;
	instagramHandle: string | null;
	videoUrl: string | null;
	displayOrder: number;
	dateCreated: string;
};

export type ProCertification = {
	id: number;
	proId: string;
	title: string;
	issuer: string | null;
	year: number | null;
	type: "certification" | "award" | "membership";
	imageUrl: string | null;
	displayOrder: number;
	dateCreated: string;
};

export type ProTestimonial = {
	id: number;
	proId: string;
	customerName: string;
	customerLocation: string | null;
	projectType: string | null;
	reviewText: string;
	rating: number | null;
	projectId: string | null;
	reviewDate: string | null;
	photoUrl: string | null;
	videoUrl: string | null;
	displayOrder: number;
	dateCreated: string;
};

export type CompanyProfile = {
	tagline: string | null;
	about: string | null;
	establishedYear: number | null;
	teamSizeCategory: string | null;
	areasServed: string[] | null;
	processDescription: string | null;
	socialLinks: {
		instagramHandle: string | null;
		facebookUrl: string | null;
		youtubeUrl: string | null;
		googleBusinessUrl: string | null;
		websiteUrl: string | null;
		linkedinUrl: string | null;
		twitterUrl: string | null;
		pinterestUrl: string | null;
	};
	leadershipTeam: ProLeadership[];
	certifications: ProCertification[];
	testimonials: ProTestimonial[];
};

export type UpdateCompanyProfileInput = {
	tagline?: string | null;
	about?: string | null;
	establishedYear?: number | null;
	teamSizeCategory?: "solo" | "2-5" | "6-10" | "11-25" | "25+" | null;
	areasServed?: string[] | null;
	processDescription?: string | null;
	instagramHandle?: string | null;
	facebookUrl?: string | null;
	youtubeUrl?: string | null;
	googleBusinessUrl?: string | null;
	websiteUrl?: string | null;
	linkedinUrl?: string | null;
	twitterUrl?: string | null;
	pinterestUrl?: string | null;
};

export type AddLeadershipInput = {
	name: string;
	role: string;
	bio?: string | null;
	photoUrl?: string | null;
	linkedinUrl?: string | null;
	instagramHandle?: string | null;
	videoUrl?: string | null;
};

export type UpdateLeadershipInput = {
	name?: string;
	role?: string;
	bio?: string | null;
	photoUrl?: string | null;
	linkedinUrl?: string | null;
	instagramHandle?: string | null;
	videoUrl?: string | null;
};

export type AddCertificationInput = {
	title: string;
	issuer?: string | null;
	year?: number | null;
	type: "certification" | "award" | "membership";
	imageUrl?: string | null;
};

export type UpdateCertificationInput = {
	title?: string;
	issuer?: string | null;
	year?: number | null;
	type?: "certification" | "award" | "membership";
	imageUrl?: string | null;
};

export type AddTestimonialInput = {
	customerName: string;
	customerLocation?: string | null;
	projectType?: string | null;
	reviewText: string;
	rating?: number | null;
	projectId?: string | null;
	reviewDate?: string | null;
	photoUrl?: string | null;
	videoUrl?: string | null;
};

export type UpdateTestimonialInput = {
	customerName?: string;
	customerLocation?: string | null;
	projectType?: string | null;
	reviewText?: string;
	rating?: number | null;
	projectId?: string | null;
	reviewDate?: string | null;
	photoUrl?: string | null;
	videoUrl?: string | null;
};

export const companyProfileApi = {
	// Company Profile
	async getCompanyProfile(proId: string) {
		return request<CompanyProfile>(`/api/pro/${proId}/company-profile`);
	},

	// Leadership Team
	async addLeadershipMember(proId: string, data: AddLeadershipInput) {
		return request<ProLeadership>(
			`/api/pro/${proId}/company-profile/leadership`,
			{
				method: "POST",
				body: data,
			},
		);
	},

	async updateLeadershipMember(
		proId: string,
		memberId: number,
		data: UpdateLeadershipInput,
	) {
		return request<ProLeadership>(
			`/api/pro/${proId}/company-profile/leadership/${memberId}`,
			{
				method: "PUT",
				body: data,
			},
		);
	},

	async deleteLeadershipMember(proId: string, memberId: number) {
		return request(
			`/api/pro/${proId}/company-profile/leadership/${memberId}`,
			{
				method: "DELETE",
			},
		);
	},

	// Certifications
	async addCertification(proId: string, data: AddCertificationInput) {
		return request<ProCertification>(
			`/api/pro/${proId}/company-profile/certifications`,
			{
				method: "POST",
				body: data,
			},
		);
	},

	async updateCertification(
		proId: string,
		certId: number,
		data: UpdateCertificationInput,
	) {
		return request<ProCertification>(
			`/api/pro/${proId}/company-profile/certifications/${certId}`,
			{
				method: "PUT",
				body: data,
			},
		);
	},

	async deleteCertification(proId: string, certId: number) {
		return request(
			`/api/pro/${proId}/company-profile/certifications/${certId}`,
			{
				method: "DELETE",
			},
		);
	},

	// Testimonials
	async addTestimonial(proId: string, data: AddTestimonialInput) {
		return request<ProTestimonial>(
			`/api/pro/${proId}/company-profile/testimonials`,
			{
				method: "POST",
				body: data,
			},
		);
	},

	async updateTestimonial(
		proId: string,
		testimonialId: number,
		data: UpdateTestimonialInput,
	) {
		return request<ProTestimonial>(
			`/api/pro/${proId}/company-profile/testimonials/${testimonialId}`,
			{
				method: "PUT",
				body: data,
			},
		);
	},

	async deleteTestimonial(proId: string, testimonialId: number) {
		return request(
			`/api/pro/${proId}/company-profile/testimonials/${testimonialId}`,
			{
				method: "DELETE",
			},
		);
	},
};
